/**
 * Staff classroom learner viewer (TRUS-225) — Google Classroom-style.
 * Left: module/lesson nav (✓/○/🔒). Right: lesson player for text / YouTube
 * video / form embed / quiz / survey. Staff auth (authFetch), enrollment keyed
 * on the staff user. Quiz lessons are graded on submit; a passing score (or a
 * survey submission) marks the lesson complete and advances the course.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  CheckCircle,
  Circle,
  BookOpen,
  Video,
  FileText,
  ListChecks,
  Play,
  Lock,
  Loader2,
  Presentation,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';
import WorkforceLayout from '../layout';
import { navigateToHubTab } from '../hubs/hubNavigation';
import { SurveyFill } from '@/components/surveys/SurveyFill';
import { slidesEmbedSrc } from '../courses/slidesEmbed';

type ContentType = 'text' | 'video' | 'form_embed' | 'quiz' | 'survey' | 'slides' | 'platform_survey';
interface Lesson {
  id: number;
  title: string;
  content_type: ContentType;
  content: Record<string, any>;
  sort_order: number;
  completed: boolean;
  translations?: Record<string, { title?: string; content?: Record<string, any> }>;
}
interface Module {
  id: number;
  title: string;
  lessons: Lesson[];
}
interface CourseDetail {
  id: number;
  title: string;
  description: string | null;
  enrollment_id: number | null;
  enrollment_status: string | null;
  modules: Module[];
  total_lessons: number;
  completed_lessons: number;
}

const activeCboId = () => localStorage.getItem('active_cbo_id') || '';
const api = (p: string) => getWeaveinApiUrl(p);

// Does any lesson carry a Spanish variant? Controls the language picker.
function courseHasSpanish(course: { modules: { lessons: Lesson[] }[] }): boolean {
  return course.modules.some((m) =>
    m.lessons.some((l) => {
      const es = l.translations?.es;
      return !!es && (!!es.title || Object.keys(es.content || {}).length > 0);
    }),
  );
}

// Merge the Spanish variant over the English base for display. Missing pieces
// fall back to English; quiz/survey questions are only swapped when they
// mirror the English structure (grading follows the English answer key).
function localizeLesson(lesson: Lesson, lang: 'en' | 'es'): Lesson {
  if (lang === 'en') return lesson;
  const es = lesson.translations?.es;
  if (!es) return lesson;
  const esContent = es.content || {};
  let content = Object.keys(esContent).length ? { ...lesson.content, ...esContent } : lesson.content;
  if (lesson.content_type === 'quiz' || lesson.content_type === 'survey') {
    const enQs = (lesson.content?.questions as any[]) || [];
    const esQs = (esContent.questions as any[]) || [];
    if (esQs.length !== enQs.length) content = { ...content, questions: enQs };
  }
  return { ...lesson, title: es.title || lesson.title, content };
}

function lessonIcon(t: string) {
  if (t === 'video') return <Video className="h-3.5 w-3.5 shrink-0" />;
  if (t === 'form_embed') return <FileText className="h-3.5 w-3.5 shrink-0" />;
  if (t === 'quiz' || t === 'survey' || t === 'platform_survey') return <ListChecks className="h-3.5 w-3.5 shrink-0" />;
  if (t === 'slides') return <Presentation className="h-3.5 w-3.5 shrink-0" />;
  return <BookOpen className="h-3.5 w-3.5 shrink-0" />;
}

export default function CourseLearnerPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const cbo = activeCboId();

  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [enrollmentId, setEnrollmentId] = useState<number | null>(null);
  const [activeLesson, setActiveLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lang, setLang] = useState<'en' | 'es'>('en');
  // quiz state
  const [answers, setAnswers] = useState<Record<number, number | string>>({});
  const [result, setResult] = useState<{ passed: boolean; score: number | null } | null>(null);

  const loadCourse = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const res = await authFetch(api(`/api/lms/staff/courses/${courseId}?cbo_id=${cbo}`));
      if (!res.ok) throw new Error('Course not found');
      const data: CourseDetail = await res.json();
      setCourse(data);
      setEnrollmentId(data.enrollment_id);
      const all = data.modules.flatMap((m) => m.lessons);
      setActiveLesson((prev) => {
        if (prev) {
          const again = all.find((l) => l.id === prev.id);
          if (again) return again;
        }
        return all.find((l) => !l.completed) ?? all[0] ?? null;
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load course');
    } finally {
      setLoading(false);
    }
  }, [courseId, cbo]);

  useEffect(() => {
    loadCourse();
  }, [loadCourse]);

  // reset quiz working state when switching lessons
  useEffect(() => {
    setAnswers({});
    setResult(null);
  }, [activeLesson?.id]);

  const handleEnroll = async () => {
    if (!courseId) return;
    setEnrolling(true);
    try {
      const res = await authFetch(api(`/api/lms/staff/courses/${courseId}/enroll?cbo_id=${cbo}`), { method: 'POST' });
      if (!res.ok) throw new Error('Enrollment failed');
      const data = await res.json();
      setEnrollmentId(data.enrollment_id);
      await loadCourse();
    } catch (err: any) {
      setError(err.message || 'Enrollment failed');
    } finally {
      setEnrolling(false);
    }
  };

  const submitLesson = async () => {
    if (!activeLesson || !enrollmentId) return;
    setSubmitting(true);
    setResult(null);
    try {
      const isAssessment = activeLesson.content_type === 'quiz' || activeLesson.content_type === 'survey';
      const body: any = { enrollment_id: enrollmentId };
      if (isAssessment) {
        const qs = (activeLesson.content?.questions as any[]) || [];
        body.answers = qs.map((q, i) =>
          i in answers ? answers[i] : (q?.options || []).length > 0 ? -1 : ''
        );
      }
      const res = await authFetch(api(`/api/lms/staff/lessons/${activeLesson.id}/complete`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Could not submit');
      const data = await res.json();
      if (isAssessment) {
        setResult({ passed: !!data.passed, score: data.score ?? null });
        if (!data.completed) return; // failed quiz — let them retry, don't reload away
      }
      await loadCourse();
    } catch (err: any) {
      setError(err.message || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <WorkforceLayout>
        <div className="flex items-center justify-center py-24 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading course…
        </div>
      </WorkforceLayout>
    );
  }
  if (!course) {
    return (
      <WorkforceLayout>
        <div className="text-center py-24 text-gray-500">
          <p className="mb-4">{error || 'Course not found.'}</p>
          <Button variant="outline" onClick={() => navigateToHubTab(navigate, 'workforce-learning-hub', 'my-learning')}>
            Back to My Learning
          </Button>
        </div>
      </WorkforceLayout>
    );
  }

  const progressPct = course.total_lessons > 0 ? Math.round((course.completed_lessons / course.total_lessons) * 100) : 0;
  const enrolled = enrollmentId !== null;
  const allLessons = course.modules.flatMap((m) => m.lessons);
  const hasSpanish = courseHasSpanish(course);
  const displayLesson = activeLesson ? localizeLesson(activeLesson, lang) : null;

  return (
    <WorkforceLayout>
      <div className="flex flex-col h-[calc(100vh-4rem)] bg-white dark:bg-gray-900">
        {/* Top bar */}
        <header className="border-b px-4 py-2 flex items-center gap-3 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateToHubTab(navigate, 'workforce-learning-hub', 'my-learning')}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">{course.title}</h1>
            {enrolled && (
              <div className="flex items-center gap-2 mt-0.5">
                <Progress value={progressPct} className="h-1.5 w-32" />
                <span className="text-xs text-gray-400">
                  {course.completed_lessons}/{course.total_lessons} lessons
                </span>
              </div>
            )}
          </div>
          {hasSpanish && (
            <div className="flex items-center rounded-lg border p-0.5 shrink-0" title="Course language">
              {(['en', 'es'] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-2 py-0.5 rounded-md text-xs font-medium transition-colors ${
                    lang === l
                      ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
                      : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {l === 'en' ? 'English' : 'Español'}
                </button>
              ))}
            </div>
          )}
          {!enrolled && (
            <Button size="sm" onClick={handleEnroll} disabled={enrolling} className="gap-1 shrink-0">
              <Play className="h-3.5 w-3.5" />
              {enrolling ? 'Enrolling…' : 'Enroll'}
            </Button>
          )}
          {enrolled && course.enrollment_status === 'completed' && (
            <Badge className="bg-green-100 text-green-700 border-green-200 shrink-0">
              <CheckCircle className="h-3 w-3 mr-1" /> Complete
            </Badge>
          )}
        </header>

        {error && <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-red-600 text-xs">{error}</div>}

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <aside className="w-64 shrink-0 border-r bg-gray-50 dark:bg-gray-800/50 overflow-y-auto">
            <div className="p-3">
              {course.modules.map((mod) => (
                <div key={mod.id} className="mb-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 px-1">{mod.title}</p>
                  <ul className="space-y-0.5">
                    {mod.lessons.map((lesson) => {
                      const isActive = activeLesson?.id === lesson.id;
                      const isLocked = !enrolled;
                      return (
                        <li key={lesson.id}>
                          <button
                            onClick={() => !isLocked && setActiveLesson(lesson)}
                            disabled={isLocked}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors ${
                              isActive
                                ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200 font-medium'
                                : isLocked
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                            }`}
                          >
                            {isLocked ? (
                              <Lock className="h-3.5 w-3.5 shrink-0 text-gray-300" />
                            ) : lesson.completed ? (
                              <CheckCircle className="h-3.5 w-3.5 shrink-0 text-green-500" />
                            ) : (
                              <Circle className="h-3.5 w-3.5 shrink-0 text-gray-300" />
                            )}
                            {lessonIcon(lesson.content_type)}
                            <span className="truncate leading-tight">{localizeLesson(lesson, lang).title}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </aside>

          {/* Player */}
          <main className="flex-1 overflow-y-auto p-6">
            {!enrolled ? (
              <div className="max-w-lg mx-auto text-center py-16">
                <BookOpen className="h-12 w-12 text-gray-200 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">Enroll to get started</h2>
                {course.description && <p className="text-sm text-gray-500 mb-6">{course.description}</p>}
                <Button onClick={handleEnroll} disabled={enrolling} className="gap-2">
                  <Play className="h-4 w-4" />
                  {enrolling ? 'Enrolling…' : 'Enroll in this course'}
                </Button>
              </div>
            ) : activeLesson && displayLesson ? (
              <div className="w-full max-w-5xl mx-auto">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <h2 className="text-xl font-semibold text-ink leading-snug">{displayLesson.title}</h2>
                  <Badge variant="outline" className="text-xs shrink-0 capitalize">
                    {displayLesson.content_type.replace('_', ' ')}
                  </Badge>
                </div>

                {displayLesson.content_type === 'platform_survey' ? (
                  displayLesson.content?.survey_id ? (
                    <SurveyFill
                      surveyId={String(displayLesson.content.survey_id)}
                      embedded
                      onCompleted={() => submitLesson()}
                    />
                  ) : (
                    <p className="text-sm text-gray-400">No survey linked to this component yet.</p>
                  )
                ) : (
                  <LessonBody
                    lesson={displayLesson}
                    answers={answers}
                    setAnswer={(qi, v) => setAnswers((a) => ({ ...a, [qi]: v }))}
                  />
                )}

                {/* Result banner */}
                {result && (
                  <div
                    className={`mt-4 p-3 rounded-lg text-sm ${
                      result.passed
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}
                  >
                    {result.passed
                      ? `Passed${result.score !== null ? ` — ${result.score}%` : ''}. Nice work!`
                      : `Score ${result.score ?? 0}% — below passing. Review and try again.`}
                  </div>
                )}

                {/* Actions */}
                <div className="mt-6 flex items-center justify-between">
                  <div className="flex gap-2">
                    {(() => {
                      const idx = allLessons.findIndex((l) => l.id === activeLesson.id);
                      const prev = allLessons[idx - 1];
                      const next = allLessons[idx + 1];
                      return (
                        <>
                          {prev && (
                            <Button variant="outline" size="sm" onClick={() => setActiveLesson(prev)}>← Previous</Button>
                          )}
                          {next && (
                            <Button variant="outline" size="sm" onClick={() => setActiveLesson(next)}>Next →</Button>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {activeLesson.completed ? (
                    <Badge className="bg-green-50 text-green-700 border-green-200 gap-1">
                      <CheckCircle className="h-3 w-3" /> Completed
                    </Badge>
                  ) : activeLesson.content_type === 'platform_survey' ? (
                    <span className="text-xs text-gray-400">Submit the survey above to complete this component.</span>
                  ) : activeLesson.content_type === 'quiz' || activeLesson.content_type === 'survey' ? (
                    <Button size="sm" onClick={submitLesson} disabled={submitting} className="gap-1.5">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />}
                      Submit {activeLesson.content_type === 'quiz' ? 'answers' : 'responses'}
                    </Button>
                  ) : (
                    <Button size="sm" onClick={submitLesson} disabled={submitting} className="gap-1.5">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                      Mark complete
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-16 text-gray-400 text-sm">Select a lesson to begin.</div>
            )}
          </main>
        </div>
      </div>
    </WorkforceLayout>
  );
}

// ---------------------------------------------------------------------------
// Lesson body renderer (text / video / form / quiz / survey)
// ---------------------------------------------------------------------------
function LessonBody({
  lesson,
  answers,
  setAnswer,
}: {
  lesson: Lesson;
  answers: Record<number, number | string>;
  setAnswer: (qi: number, v: number | string) => void;
}) {
  const content = lesson.content || {};

  if (lesson.content_type === 'video') {
    const url = content.url || '';
    const embedUrl = String(url).replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/');
    return (
      <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
        {embedUrl ? (
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={lesson.title}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-white text-sm">No video URL provided.</div>
        )}
      </div>
    );
  }

  if (lesson.content_type === 'slides') {
    const rawUrl = content.embed_url || content.url || '';
    const src = slidesEmbedSrc(content.provider, rawUrl);
    return (
      <div className="aspect-video w-full rounded-lg overflow-hidden border bg-gray-100 dark:bg-gray-800">
        {src ? (
          <iframe
            src={src}
            className="w-full h-full"
            allowFullScreen
            allow="fullscreen"
            title={lesson.title}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">No slides URL configured.</div>
        )}
      </div>
    );
  }

  if (lesson.content_type === 'form_embed') {
    const src = content.embed_url || content.url || '';
    return (
      <div className="w-full rounded-lg overflow-hidden border">
        {src ? (
          <iframe src={src} className="w-full min-h-[600px]" title={lesson.title} />
        ) : (
          <div className="p-8 text-center text-gray-400 text-sm">No form URL configured.</div>
        )}
      </div>
    );
  }

  if (lesson.content_type === 'quiz' || lesson.content_type === 'survey') {
    const questions = (content.questions as any[]) || [];
    if (questions.length === 0) {
      return <p className="text-sm text-gray-400">No questions configured for this assessment.</p>;
    }
    return (
      <div className="space-y-5">
        {questions.map((q: any, qi: number) => (
          <div key={qi} className="border rounded-lg p-4">
            <p className="font-medium text-gray-800 dark:text-gray-100 mb-3">
              {qi + 1}. {q.prompt}
            </p>
            {(q.options || []).length === 0 ? (
              <textarea
                rows={3}
                value={typeof answers[qi] === 'string' ? (answers[qi] as string) : ''}
                onChange={(e) => setAnswer(qi, e.target.value)}
                placeholder="Type your answer…"
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            ) : (
            <div className="space-y-2">
              {(q.options || []).map((opt: string, oi: number) => (
                <label
                  key={oi}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                    answers[qi] === oi
                      ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/30'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <input
                    type="radio"
                    name={`q-${qi}`}
                    checked={answers[qi] === oi}
                    onChange={() => setAnswer(qi, oi)}
                    className="accent-teal-600"
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // text / default
  const html = content.html || content.body || '';
  const text = content.text || '';
  return (
    <div className="prose prose-sm max-w-none text-gray-800 dark:text-gray-200 dark:prose-invert">
      {html ? <div dangerouslySetInnerHTML={{ __html: html }} /> : <p className="whitespace-pre-wrap">{text || 'No content available.'}</p>}
    </div>
  );
}
